/***
 *
 *   FREE TIER UPGRADE
 *   Upgrade from free tier to paid plan with credit card.
 *
 **********/

import { useContext, useState, useEffect } from "react";
import {
  AuthContext,
  PaymentForm,
  usePlans,
  Event,
  useNavigate,
  Animate,
} from "components/lib";

export function FreeTierUpgrade({ t }) {
  const navigate = useNavigate();
  const plans = usePlans();

  // context
  const authContext = useContext(AuthContext);

  // state
  const [inputs, setInputs] = useState(null);
  const [cardData, setCardData] = useState({});

  // Redirect if not on free plan
  useEffect(() => {
    if (authContext.user?.plan && authContext.user.plan !== "free") {
      navigate("/billing");
    }
  }, [authContext.user?.plan, navigate]);

  useEffect(() => {
    if (plans.data?.list?.length) {
      // Find the first paid plan
      const paidPlan = plans.data.list.find((p) => p.value !== "free");
      if (paidPlan) {
        setInputs({
          plan: {
            type: "hidden",
            value: paidPlan.value,
            required: true,
            readonly: true,
          },
          token: {
            label: "Card Details",
            type: "creditcard",
            required: true,
          },
        });
        setCardData({
          name: {
            label: "Cardholder Name",
            type: "text",
            required: true,
            placeholder: "John Doe",
          },
          email: {
            label: "Billing Email",
            type: "email",
            required: true,
            placeholder: "john@example.com",
          },
          address_line1: {
            label: "Address",
            type: "text",
            required: true,
            placeholder: "123 Main St",
          },
          address_city: {
            label: "City",
            type: "text",
            required: true,
            placeholder: "New York",
          },
          state: {
            label: "State",
            type: "text",
            required: true,
            placeholder: "NY",
          },
          address_zip: {
            label: "ZIP Code",
            type: "text",
            required: true,
            placeholder: "10001",
          },
          address_country: {
            label: "Country",
            type: "text",
            required: true,
            placeholder: "US",
          },
        });
      }
    }
  }, [plans, t]);

  const handleUpgradeCallback = (res) => {
    Event({
      name: "upgraded_from_free",
      metadata: { plan: res.data.data.plan },
    });
    authContext.update({
      plan: res.data.data.plan,
      subscription: "active",
    });
    navigate("/dashboard");
  };

  return (
    <Animate>
      <div className="max-w-2xl mx-auto">
        <div className="glass-card dark:glass-card-dark backdrop-blur-lg border border-white/10 rounded-xl pb-4 shadow-2xl">
          <div className="px-5 py-3 pb-6 border-b border-white/10">
            <h2 className="text-xl font-semibold text-white poppins-semibold">
              Upgrade to Pay-as-you-go
            </h2>
            <p className="text-sm text-white/60 mt-1 poppins-light">
              Unlock unlimited API requests and all features
            </p>
          </div>

          {/* Current Plan Info */}
          <div className="px-5 py-4">
            <div className="glass-card dark:glass-card-dark backdrop-blur-lg border border-white/10 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center mb-3">
                <span className="text-white/60 poppins-light">Current Plan</span>
                <span className="font-medium text-orange-400 poppins-regular">Free Tier</span>
              </div>
              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-white/50 poppins-light">Maps API</span>
                  <span className="text-white poppins-regular">10,000 requests/month</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50 poppins-light">Geocode API</span>
                  <span className="text-white poppins-regular">25,000 requests/month</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50 poppins-light">Other APIs</span>
                  <span className="text-white poppins-regular">100,000 requests/month</span>
                </div>
              </div>
            </div>

            {/* New Plan Info */}
            <div className="glass-card dark:glass-card-dark backdrop-blur-lg border border-blue-400/30 rounded-lg p-4 mb-6 bg-blue-500/10">
              <h3 className="font-semibold text-blue-300 mb-2 poppins-medium">Pay-as-you-go Plan</h3>
              <ul className="text-sm text-blue-200/90 space-y-1 poppins-light">
                <li>• Unlimited API requests for all scopes</li>
                <li>• Access to all APIs (maps, geocode, directions, etc.)</li>
                <li>• Only pay for what you use</li>
                <li>• Free tier still included each month</li>
              </ul>
            </div>
          </div>

          {/* Payment Form */}
          {inputs ? (
            <PaymentForm
              card_metadata={cardData}
              inputs={inputs}
              url="/api/account/upgrade"
              method="POST"
              buttonText="Upgrade & Pay"
              callback={handleUpgradeCallback}
            />
          ) : (
            <div className="px-5 py-8 flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-400 mb-3"></div>
              <p className="text-sm text-white/60 poppins-light">Loading payment form...</p>
              <p className="text-xs text-white/40 mt-1 poppins-light">This may take a moment</p>
            </div>
          )}

          <div className="px-5 mt-4">
            <button
              type="button"
              onClick={() => navigate("/billing")}
              className="text-sm text-white/60 hover:text-white/90 poppins-light transition-colors duration-300"
            >
              ← Back to Billing
            </button>
          </div>
        </div>
      </div>
    </Animate>
  );
}
